import * as babylon from "@babylonjs/core";

import type { OrbitCamera } from "@/shared/state/visualization/schema/v1/three-d";

import type { BabylonState } from "../../state";

import { targetPosition } from "./resolveGlobalPositions";

/**
 * Reused across the whole call so a 60fps loop allocates nothing of its own.
 * Safe as module state for the same reason `resolveGlobalPositions`' scratch is:
 * this runs to completion synchronously on one thread, and none of these values
 * outlives a single call.
 */
const ANCHOR_AT = babylon.Vector3.Zero();
const NODE_AT = babylon.Vector3.Zero();
const OFFSET = babylon.Vector3.Zero();

/** What an orbit camera follows. */
export type OrbitTarget = OrbitCamera["target"];

/**
 * The absolute data-space point an orbit camera follows.
 *
 * {@link targetPosition} for a target that names an entity and nothing more —
 * the entity's own transform, which is what an orbit camera has always tracked.
 * When the target also names a **node inside the model** the entity draws, that
 * node's position instead: a gripper at the end of an arm rather than the base
 * link the arm is bolted to.
 *
 * ## Why the node's world position is not the answer
 *
 * A model node is not an entity. Its pose comes from a Babylon transform under
 * `model.anchor`, and `applyRenderOrigin` moves that anchor by
 * `-camera.focusPoint` every frame — so the node's *world* position is stated
 * relative to wherever the camera currently looks, and a focus point derived
 * from one would be an input to its own computation.
 *
 * What is taken here is the node's position **relative to the anchor**, as the
 * difference of the two world translations. Both carry the same `-focusPoint`
 * term and it cancels in the subtraction, so `focusPoint` appears nowhere in the
 * result: there is no loop, and nothing to settle. `applyRenderOrigin` writes
 * *only* `model.anchor.position` and never touches anything below it (see
 * `poseJoints`), which is what makes that cancellation exact rather than
 * approximate.
 *
 * ## Why it is the world matrices and not the local ones
 *
 * A node's own `position` is relative to its **parent**, not to the anchor. A
 * gripper hangs several joints down — through a shoulder, an elbow, a wrist,
 * each with its own rotation — and its offset from the anchor is only reached by
 * composing every one of those, each translation turned by its parent's
 * accumulated orientation. That is precisely the accumulation
 * `resolveGlobalPositions` performs by hand for *entities*, and precisely what
 * Babylon already maintains for nodes. So this reads what Babylon computed:
 * `node.worldMatrix - anchor.worldMatrix` in translation is the translation part
 * of `anchor.worldMatrix⁻¹ · node.worldMatrix` carried back into world
 * orientation — the whole chain, correct at any depth, and correct through the
 * anchor's own rotation and scale because both matrices carry them.
 *
 * The result is also *live*: the joints between the anchor and the node move
 * within the anchor's frame, so a gripper that swings is followed as it swings.
 * That is the whole point of naming a node, and the reason this is not measured
 * once at import.
 *
 * ## Where this sits in the frame
 *
 * `render` runs `poseJoints` (which writes each driven joint's **local**
 * transform), then `resolveGlobalPositions`, then `updateCamera` — this — then
 * `applyRenderOrigin` (which writes `anchor.position`), then `scene.render`
 * (which recomputes world matrices). So at the moment this runs, the joint
 * *locals* are this frame's and the world matrices are last frame's. Reading
 * them as they stand would put the camera a frame behind the robot it is
 * following, which is exactly the artefact a chase camera makes visible, so the
 * chain is recomputed here instead. `computeWorldMatrix(true)` recurses into the
 * parent, so one call composes anchor-to-node afresh — a handful of matrix
 * multiplies down a single chain, not the whole-subtree pass `meshBounds`
 * documents avoiding. The anchor's own position is still last frame's, and does
 * not matter: it cancels.
 *
 * The offset carries **float32** precision, because a Babylon world matrix is a
 * `Float32Array` — so it is exact to the render-space magnitude the model is
 * drawn at rather than to the data-space one it is added to. That is the
 * arrangement the render origin exists to produce and the reason it follows the
 * camera: the anchor is never far from the origin in a scene anyone is looking
 * at, and a metre-scale robot near it resolves to well under a micron.
 *
 * ## The cases with no node to find
 *
 * A `path` target is untouched — its position is the mean of the whole
 * trajectory, chosen so the camera frames the trace rather than chasing the
 * object along it, and a node inside a model has nothing to say about that.
 *
 * A named node that the model does not have falls back to the entity's own
 * position rather than throwing or drifting to the world origin. That happens
 * for real: a re-picked model, or a source description re-ingested with a link
 * renamed. The name is kept in the panel state either way, and the camera
 * settings are where the user is told the model has no such node — the same
 * treatment an orphaned joint binding gets.
 *
 * @param out written only on success, so a caller may pass the live
 *   `camera.focusPoint` and have an unresolvable target leave it where it was.
 * @returns `out`, or `undefined` when the target names no entity with a resolved
 *   global position — one still loading its data, or one already removed.
 */
export function targetFocus(
  state: BabylonState,
  target: OrbitTarget,
  out: babylon.Vector3,
): babylon.Vector3 | undefined {
  const source = targetPosition(state, target);
  if (!source) {
    return undefined;
  }
  if (target.mesh === "path" || target.node === undefined) {
    return out.copyFrom(source);
  }

  const model = state.entities[target.id]?.mesh?.model;
  const node = model?.nodesByName.get(target.node);
  if (!model || !node) {
    return out.copyFrom(source);
  }

  // Forced, and in this order. `poseJoints` wrote this frame's joint locals a
  // few systems ago but nothing has composed them into world matrices yet —
  // `scene.render` does that at the end of the frame — so the cached matrices
  // are last frame's pose. `computeWorldMatrix(true)` recurses into the parent,
  // so the second call composes the entire anchor-to-node chain; the first is
  // for the anchor's own sake, since a node that somehow does not hang below it
  // would otherwise be differenced against a stale anchor.
  model.anchor.computeWorldMatrix(true).getTranslationToRef(ANCHOR_AT);
  node.computeWorldMatrix(true).getTranslationToRef(NODE_AT);
  NODE_AT.subtractToRef(ANCHOR_AT, OFFSET);
  if (!isFinitePoint(OFFSET)) {
    return out.copyFrom(source);
  }
  return out.copyFrom(source).addInPlace(OFFSET);
}

/** Whether every coordinate is a real number. */
function isFinitePoint(point: babylon.Vector3): boolean {
  return (
    Number.isFinite(point.x) &&
    Number.isFinite(point.y) &&
    Number.isFinite(point.z)
  );
}
